/**
 * useWebRTCFirebase - WebRTC Hook using Firebase Firestore for Signaling
 * No separate signaling server needed - everything runs through Firebase!
 * Includes Waiting Room feature for host to admit/decline guests
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { db } from '../firebase';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  onSnapshot, 
  updateDoc, 
  deleteDoc,
  addDoc,
  serverTimestamp,
  query,
  orderBy
} from 'firebase/firestore';

// STUN/TURN servers for NAT traversal
const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
    { urls: 'stun:stun3.l.google.com:19302' },
    { urls: 'stun:stun4.l.google.com:19302' },
    // Free TURN server for better connectivity
    {
      urls: 'turn:openrelay.metered.ca:80',
      username: 'openrelayproject',
      credential: 'openrelayproject'
    },
    {
      urls: 'turn:openrelay.metered.ca:443',
      username: 'openrelayproject',
      credential: 'openrelayproject'
    }
  ]
};

export function useWebRTCFirebase() {
  const [isConnected, setIsConnected] = useState(false);
  const [roomId, setRoomId] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState(new Map());
  const [participants, setParticipants] = useState(new Map());
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenShareStream, setScreenShareStream] = useState(null);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [error, setError] = useState(null);
  
  // Waiting room states
  const [isHost, setIsHost] = useState(false);
  const [waitingGuests, setWaitingGuests] = useState(new Map());
  const [waitingStatus, setWaitingStatus] = useState(null); // 'waiting', 'admitted', 'declined'

  const peerConnectionsRef = useRef(new Map());
  const localStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const originalVideoTrackRef = useRef(null);
  const userIdRef = useRef(null);
  const userNameRef = useRef(null);
  const unsubscribersRef = useRef([]);
  const roomDocRef = useRef(null);
  const participantDocRef = useRef(null);
  const currentRoomIdRef = useRef(null);
  const isHostRef = useRef(false);

  // Cleanup function for Firestore listeners
  const cleanupListeners = useCallback(() => {
    unsubscribersRef.current.forEach(unsub => {
      try {
        unsub();
      } catch (e) {
        console.log('Error unsubscribing:', e);
      }
    });
    unsubscribersRef.current = [];
  }, []);

  // Create a placeholder stream when no camera/mic available
  const createPlaceholderStream = useCallback(() => {
    console.log('📺 Creating placeholder stream (no camera/mic)');
    
    // Create a blank canvas for video placeholder
    const canvas = document.createElement('canvas');
    canvas.width = 640;
    canvas.height = 480;
    const ctx = canvas.getContext('2d');
    
    // Draw a nice placeholder
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    const canvasStream = canvas.captureStream(1);
    return canvasStream;
  }, []);

  // Get local media stream - asks once, works with or without permission
  const getLocalStream = useCallback(async () => {
    let stream = null;
    let hasVideo = false;
    let hasAudio = false;

    try {
      // Ask for camera and microphone permission once
      console.log('🎥 Requesting camera/microphone permission...');
      stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          width: { ideal: 1920, min: 1280 }, 
          height: { ideal: 1080, min: 720 }, 
          facingMode: 'user',
          frameRate: { ideal: 30, max: 60 }
        },
        audio: { 
          echoCancellation: true, 
          noiseSuppression: true, 
          autoGainControl: true,
          sampleRate: 48000,
          channelCount: 2
        }
      });
      hasVideo = stream.getVideoTracks().length > 0;
      hasAudio = stream.getAudioTracks().length > 0;
      console.log('✅ Got full media access - video:', hasVideo, 'audio:', hasAudio);
    } catch (err) {
      console.log('⚠️ Full media failed:', err.name, '- trying alternatives...');
      
      // Try audio only
      if (!stream) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
          hasAudio = true;
          console.log('✅ Got audio only');
        } catch (e) {
          console.log('⚠️ Audio only failed:', e.name);
        }
      }
      
      // Try video only
      if (!stream) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
          hasVideo = true;
          console.log('✅ Got video only');
        } catch (e) {
          console.log('⚠️ Video only failed:', e.name);
        }
      }
    }

    // If still no stream, create placeholder - user can still join!
    if (!stream) {
      console.log('📺 No media access - using placeholder stream');
      stream = createPlaceholderStream();
      hasVideo = false;
      hasAudio = false;
    }

    // Store the original video track if we have one
    const videoTrack = stream.getVideoTracks()[0];
    if (videoTrack) {
      originalVideoTrackRef.current = videoTrack;
      console.log('📹 Video track stored:', videoTrack.label, 'enabled:', videoTrack.enabled);
    }

    localStreamRef.current = stream;
    console.log('📺 Setting localStream state with tracks:', stream.getTracks().map(t => t.kind));
    setLocalStream(stream);
    setVideoEnabled(hasVideo);
    setAudioEnabled(hasAudio);

    // Show friendly message about media status
    if (!hasVideo && !hasAudio) {
      console.log('ℹ️ Joining without camera/microphone');
    } else if (!hasVideo) {
      console.log('ℹ️ Joining with microphone only');
    } else if (!hasAudio) {
      console.log('ℹ️ Joining with camera only');
    }

    return stream;
  }, [createPlaceholderStream]);

  // Create peer connection for a participant
  const createPeerConnection = useCallback(async (participantId, isInitiator = false) => {
    console.log(`🔗 Creating peer connection for ${participantId}, initiator: ${isInitiator}`);
    
    const pc = new RTCPeerConnection(ICE_SERVERS);
    peerConnectionsRef.current.set(participantId, pc);

    // Add local tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current);
      });
    }

    // Handle ICE candidates
    const roomIdForCandidates = currentRoomIdRef.current;
    const myUserId = userIdRef.current;
    
    pc.onicecandidate = async (event) => {
      if (event.candidate && roomIdForCandidates) {
        console.log('📤 Sending ICE candidate');
        try {
          const candidatesRef = collection(
            db, 
            'rooms', 
            roomIdForCandidates, 
            'participants', 
            myUserId,
            isInitiator ? 'callerCandidates' : 'calleeCandidates'
          );
          await addDoc(candidatesRef, {
            ...event.candidate.toJSON(),
            forParticipant: participantId
          });
        } catch (e) {
          console.log('Error sending ICE candidate:', e);
        }
      }
    };

    // Handle connection state
    pc.onconnectionstatechange = () => {
      console.log(`📡 Connection state for ${participantId}:`, pc.connectionState);
      if (pc.connectionState === 'connected') {
        setConnectionState('in-call');
      }
    };

    // Handle remote tracks
    pc.ontrack = (event) => {
      console.log(`📥 Received remote track from ${participantId}`);
      const [remoteStream] = event.streams;
      setRemoteStreams(prev => new Map(prev).set(participantId, remoteStream));
    };

    return pc;
  }, []);

  // Join a room
  const joinRoom = useCallback(async (roomIdToJoin, oderId, userName, isCreator = false) => {
    try {
      setError(null);
      setConnectionState('connecting');
      
      userIdRef.current = oderId;
      userNameRef.current = userName;
      
      const finalRoomId = roomIdToJoin.toLowerCase().trim();
      setRoomId(finalRoomId);
      currentRoomIdRef.current = finalRoomId;
      
      // Get local stream first
      await getLocalStream(true, true);
      
      // Reference to the room document
      const roomRef = doc(db, 'rooms', finalRoomId);
      roomDocRef.current = roomRef;
      
      // Check if room exists
      const roomSnapshot = await getDoc(roomRef);
      
      if (!roomSnapshot.exists()) {
        // Creating new room - user becomes host
        console.log('🏠 Creating new room:', finalRoomId);
        await setDoc(roomRef, {
          createdAt: serverTimestamp(),
          createdBy: oderId,
          creatorName: userName,
          hostId: oderId
        });
        setIsHost(true);
        isHostRef.current = true;
        
        // Host joins directly as participant
        await joinAsParticipant(finalRoomId, oderId, userName);
        
        // Host listens for waiting room guests
        listenForWaitingGuests(finalRoomId);
        
      } else {
        // Room exists - check if user is host
        const roomData = roomSnapshot.data();
        
        if (roomData.hostId === oderId || roomData.createdBy === oderId || isCreator) {
          // User is the host, join directly
          console.log('👑 Joining as host');
          setIsHost(true);
          isHostRef.current = true;
          await joinAsParticipant(finalRoomId, oderId, userName);
          listenForWaitingGuests(finalRoomId);
        } else {
          // Guest - add to waiting room
          console.log('🚪 Joining waiting room...');
          setWaitingStatus('waiting');
          setConnectionState('waiting');
          
          const waitingRef = doc(db, 'rooms', finalRoomId, 'waitingRoom', oderId);
          await setDoc(waitingRef, {
            oderId: oderId,
            userName: userName,
            requestedAt: serverTimestamp(),
            status: 'waiting' // waiting, admitted, declined
          });
          
          // Listen for admission status
          const unsubWaiting = onSnapshot(waitingRef, async (waitingDoc) => {
            if (waitingDoc.exists()) {
              const data = waitingDoc.data();
              console.log('🚦 Waiting status:', data.status);
              
              if (data.status === 'admitted') {
                setWaitingStatus('admitted');
                // Remove from waiting room
                await deleteDoc(waitingRef);
                // Join as participant
                await joinAsParticipant(finalRoomId, oderId, userName);
              } else if (data.status === 'declined') {
                setWaitingStatus('declined');
                setConnectionState('declined');
                // Clean up
                if (localStreamRef.current) {
                  localStreamRef.current.getTracks().forEach(track => track.stop());
                }
                // Remove from waiting room after a delay
                setTimeout(async () => {
                  try {
                    await deleteDoc(waitingRef);
                  } catch (e) {
                    console.log('Error removing declined guest:', e);
                  }
                }, 2000);
              }
            }
          });
          unsubscribersRef.current.push(unsubWaiting);
        }
      }
      
      console.log('✅ Room operation complete:', finalRoomId);
      
    } catch (err) {
      console.error('❌ Error joining room:', err);
      setError(err.message || 'Failed to join room');
      setConnectionState('error');
      throw err;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getLocalStream, createPeerConnection]);

  // Join as an admitted participant
  const joinAsParticipant = useCallback(async (finalRoomId, oderId, userName) => {
    try {
      // Add self as participant
      const participantRef = doc(db, 'rooms', finalRoomId, 'participants', oderId);
      participantDocRef.current = participantRef;
      
      await setDoc(participantRef, {
        oderId: oderId,
        userName: userName,
        joinedAt: serverTimestamp(),
        audioEnabled: true,
        videoEnabled: true,
        isScreenSharing: false
      });
      
      setIsConnected(true);
      setConnectionState('in-call');
      setWaitingStatus(null);
      
      // Listen for other participants
      const participantsRef = collection(db, 'rooms', finalRoomId, 'participants');
      const unsubParticipants = onSnapshot(participantsRef, async (snapshot) => {
        for (const change of snapshot.docChanges()) {
          const participantData = change.doc.data();
          const participantId = change.doc.id;
          
          // Skip self
          if (participantId === oderId) continue;
          
          if (change.type === 'added') {
            console.log('👤 Participant joined:', participantData.userName);
            
            setParticipants(prev => new Map(prev).set(participantId, {
              oderId: participantData.oderId,
              userName: participantData.userName,
              audioEnabled: participantData.audioEnabled,
              videoEnabled: participantData.videoEnabled,
              isScreenSharing: participantData.isScreenSharing
            }));
            
            // Determine who initiates (alphabetically smaller ID initiates)
            const isInitiator = oderId < participantId;
            
            if (isInitiator) {
              const pc = await createPeerConnection(participantId, true);
              const offer = await pc.createOffer();
              await pc.setLocalDescription(offer);
              
              const offerRef = doc(db, 'rooms', finalRoomId, 'participants', participantId, 'offers', oderId);
              await setDoc(offerRef, {
                type: offer.type,
                sdp: offer.sdp,
                from: oderId,
                fromName: userName
              });
              
              // Listen for answer
              const answerRef = doc(db, 'rooms', finalRoomId, 'participants', oderId, 'answers', participantId);
              const unsubAnswer = onSnapshot(answerRef, async (answerDoc) => {
                if (answerDoc.exists() && pc.currentRemoteDescription === null) {
                  const answer = answerDoc.data();
                  console.log('📥 Received answer from', participantId);
                  try {
                    await pc.setRemoteDescription(new RTCSessionDescription(answer));
                  } catch (e) {
                    console.log('Error setting remote description:', e);
                  }
                }
              });
              unsubscribersRef.current.push(unsubAnswer);
              
              // Listen for callee's ICE candidates
              const calleeCandidatesRef = collection(db, 'rooms', finalRoomId, 'participants', participantId, 'calleeCandidates');
              const unsubCalleeCandidates = onSnapshot(query(calleeCandidatesRef, orderBy('__name__')), (candidateSnapshot) => {
                candidateSnapshot.docChanges().forEach(async (candidateChange) => {
                  if (candidateChange.type === 'added') {
                    const candidateData = candidateChange.doc.data();
                    if (candidateData.forParticipant === oderId) {
                      try {
                        const candidate = new RTCIceCandidate(candidateData);
                        await pc.addIceCandidate(candidate);
                      } catch (e) {
                        console.log('Error adding ICE candidate:', e);
                      }
                    }
                  }
                });
              });
              unsubscribersRef.current.push(unsubCalleeCandidates);
            } else {
              // Listen for offers
              const offersRef = collection(db, 'rooms', finalRoomId, 'participants', oderId, 'offers');
              const unsubOffers = onSnapshot(offersRef, async (offerSnapshot) => {
                for (const offerChange of offerSnapshot.docChanges()) {
                  if (offerChange.type === 'added') {
                    const offerData = offerChange.doc.data();
                    const fromId = offerChange.doc.id;
                    
                    if (fromId === participantId) {
                      console.log('📥 Received offer from', offerData.fromName);
                      
                      const pc = await createPeerConnection(participantId, false);
                      await pc.setRemoteDescription(new RTCSessionDescription(offerData));
                      
                      const answer = await pc.createAnswer();
                      await pc.setLocalDescription(answer);
                      
                      const answerRef = doc(db, 'rooms', finalRoomId, 'participants', fromId, 'answers', oderId);
                      await setDoc(answerRef, {
                        type: answer.type,
                        sdp: answer.sdp,
                        from: oderId,
                        fromName: userName
                      });
                      
                      // Listen for caller's ICE candidates
                      const callerCandidatesRef = collection(db, 'rooms', finalRoomId, 'participants', participantId, 'callerCandidates');
                      const unsubCallerCandidates = onSnapshot(query(callerCandidatesRef, orderBy('__name__')), (candidateSnapshot) => {
                        candidateSnapshot.docChanges().forEach(async (candidateChange) => {
                          if (candidateChange.type === 'added') {
                            const candidateData = candidateChange.doc.data();
                            if (candidateData.forParticipant === oderId) {
                              try {
                                const candidate = new RTCIceCandidate(candidateData);
                                await pc.addIceCandidate(candidate);
                              } catch (e) {
                                console.log('Error adding ICE candidate:', e);
                              }
                            }
                          }
                        });
                      });
                      unsubscribersRef.current.push(unsubCallerCandidates);
                    }
                  }
                }
              });
              unsubscribersRef.current.push(unsubOffers);
            }
          } else if (change.type === 'modified') {
            setParticipants(prev => {
              const updated = new Map(prev);
              const existing = updated.get(participantId);
              if (existing) {
                updated.set(participantId, {
                  ...existing,
                  audioEnabled: participantData.audioEnabled,
                  videoEnabled: participantData.videoEnabled,
                  isScreenSharing: participantData.isScreenSharing
                });
              }
              return updated;
            });
          } else if (change.type === 'removed') {
            console.log('👋 Participant left:', participantData.userName);
            
            const pc = peerConnectionsRef.current.get(participantId);
            if (pc) {
              pc.close();
              peerConnectionsRef.current.delete(participantId);
            }
            
            setParticipants(prev => {
              const updated = new Map(prev);
              updated.delete(participantId);
              return updated;
            });
            setRemoteStreams(prev => {
              const updated = new Map(prev);
              updated.delete(participantId);
              return updated;
            });
          }
        }
      });
      unsubscribersRef.current.push(unsubParticipants);
      
    } catch (err) {
      console.error('❌ Error in joinAsParticipant:', err);
      throw err;
    }
  }, [createPeerConnection]);

  // Listen for guests in the waiting room (host only)
  const listenForWaitingGuests = useCallback((finalRoomId) => {
    console.log('👀 Listening for waiting guests...');
    const waitingRoomRef = collection(db, 'rooms', finalRoomId, 'waitingRoom');
    
    const unsubWaitingRoom = onSnapshot(waitingRoomRef, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        const guestData = change.doc.data();
        const guestId = change.doc.id;
        
        if (change.type === 'added' && guestData.status === 'waiting') {
          console.log('🚪 Guest requesting to join:', guestData.userName);
          setWaitingGuests(prev => {
            const updated = new Map(prev);
            updated.set(guestId, {
              oderId: guestData.oderId,
              userName: guestData.userName,
              requestedAt: guestData.requestedAt
            });
            return updated;
          });
        } else if (change.type === 'removed') {
          setWaitingGuests(prev => {
            const updated = new Map(prev);
            updated.delete(guestId);
            return updated;
          });
        }
      });
    });
    unsubscribersRef.current.push(unsubWaitingRoom);
  }, []);

  // Admit a guest from waiting room
  const admitGuest = useCallback(async (guestId) => {
    if (!currentRoomIdRef.current || !isHostRef.current) return;
    
    console.log('✅ Admitting guest:', guestId);
    const waitingRef = doc(db, 'rooms', currentRoomIdRef.current, 'waitingRoom', guestId);
    
    try {
      await updateDoc(waitingRef, { status: 'admitted' });
      setWaitingGuests(prev => {
        const updated = new Map(prev);
        updated.delete(guestId);
        return updated;
      });
    } catch (e) {
      console.error('Error admitting guest:', e);
    }
  }, []);

  // Decline a guest from waiting room
  const declineGuest = useCallback(async (guestId) => {
    if (!currentRoomIdRef.current || !isHostRef.current) return;
    
    console.log('❌ Declining guest:', guestId);
    const waitingRef = doc(db, 'rooms', currentRoomIdRef.current, 'waitingRoom', guestId);
    
    try {
      await updateDoc(waitingRef, { status: 'declined' });
      setWaitingGuests(prev => {
        const updated = new Map(prev);
        updated.delete(guestId);
        return updated;
      });
    } catch (e) {
      console.error('Error declining guest:', e);
    }
  }, []);

  // Leave the room
  const leaveRoom = useCallback(async () => {
    console.log('👋 Leaving room...');
    
    cleanupListeners();
    
    if (participantDocRef.current) {
      try {
        await deleteDoc(participantDocRef.current);
      } catch (e) {
        console.log('Error removing participant doc:', e);
      }
    }
    
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => track.stop());
      screenStreamRef.current = null;
    }
    
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    
    peerConnectionsRef.current.forEach(pc => pc.close());
    peerConnectionsRef.current.clear();
    
    setLocalStream(null);
    setRemoteStreams(new Map());
    setParticipants(new Map());
    setRoomId(null);
    setIsConnected(false);
    setIsScreenSharing(false);
    setAudioEnabled(true);
    setVideoEnabled(true);
    setConnectionState('disconnected');
    setIsHost(false);
    setWaitingGuests(new Map());
    setWaitingStatus(null);
    isHostRef.current = false;
    originalVideoTrackRef.current = null;
    roomDocRef.current = null;
    participantDocRef.current = null;
    currentRoomIdRef.current = null;
  }, [cleanupListeners]);

  // Toggle audio
  const toggleAudio = useCallback(async () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setAudioEnabled(audioTrack.enabled);
        
        if (participantDocRef.current) {
          try {
            await updateDoc(participantDocRef.current, { audioEnabled: audioTrack.enabled });
          } catch (e) {
            console.log('Error updating audio state:', e);
          }
        }
      }
    }
  }, []);

  // Toggle video
  const toggleVideo = useCallback(async () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setVideoEnabled(videoTrack.enabled);
        
        if (participantDocRef.current) {
          try {
            await updateDoc(participantDocRef.current, { videoEnabled: videoTrack.enabled });
          } catch (e) {
            console.log('Error updating video state:', e);
          }
        }
      }
    }
  }, []);

  // Toggle screen sharing - keeps video and screen share separate
  const toggleScreenShare = useCallback(async () => {
    if (isScreenSharing) {
      // Stop screen sharing
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
        screenStreamRef.current = null;
      }
      
      // Restore original video track to peer connections
      if (originalVideoTrackRef.current && localStreamRef.current) {
        for (const pc of peerConnectionsRef.current.values()) {
          const sender = pc.getSenders().find(s => s.track?.kind === 'video');
          if (sender) {
            await sender.replaceTrack(originalVideoTrackRef.current);
          }
        }
      }
      
      setScreenShareStream(null);
      setIsScreenSharing(false);
      
      if (participantDocRef.current) {
        try {
          await updateDoc(participantDocRef.current, { isScreenSharing: false });
        } catch (e) {
          console.log('Error updating screen share state:', e);
        }
      }
    } else {
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: { 
            cursor: 'always',
            width: { ideal: 1920, max: 2560 },
            height: { ideal: 1080, max: 1440 },
            frameRate: { ideal: 30, max: 60 }
          },
          audio: true // Include audio if user shares tab with audio
        });
        
        screenStreamRef.current = screenStream;
        const screenTrack = screenStream.getVideoTracks()[0];
        
        // Store original video track for restoration
        if (!originalVideoTrackRef.current && localStreamRef.current) {
          originalVideoTrackRef.current = localStreamRef.current.getVideoTracks()[0];
        }
        
        screenTrack.onended = () => toggleScreenShare();
        
        // Replace video track with screen track in peer connections
        for (const pc of peerConnectionsRef.current.values()) {
          const sender = pc.getSenders().find(s => s.track?.kind === 'video');
          if (sender) await sender.replaceTrack(screenTrack);
        }
        
        // Set screen share stream separately (don't replace local stream)
        setScreenShareStream(new MediaStream([screenTrack]));
        setIsScreenSharing(true);
        
        if (participantDocRef.current) {
          try {
            await updateDoc(participantDocRef.current, { isScreenSharing: true });
          } catch (e) {
            console.log('Error updating screen share state:', e);
          }
        }
      } catch (err) {
        console.error('Error sharing screen:', err);
        if (err.name !== 'NotAllowedError') {
          setError('Failed to share screen');
        }
      }
    }
  }, [isScreenSharing]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      leaveRoom();
    };
  }, [leaveRoom]);

  return {
    isConnected,
    roomId,
    localStream,
    screenShareStream,
    remoteStreams,
    participants,
    audioEnabled,
    videoEnabled,
    isScreenSharing,
    connectionState,
    error,
    // Waiting room
    isHost,
    waitingGuests,
    waitingStatus,
    // Actions
    joinRoom,
    leaveRoom,
    toggleAudio,
    toggleVideo,
    toggleScreenShare,
    admitGuest,
    declineGuest,
    setError
  };
}

export default useWebRTCFirebase;
